import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from "prop-types";
import { useEffect, useState } from "react";

import styled from "styled-components/macro";

import { AnimatePresence, motion, useAnimation } from "framer-motion";

import FnrIcon from "UI/FnrIcon/FnrIcon";
import FnrInput2 from "UI/FnrInput2/FnrInput2";
import { FnrToggle, FnrToggleChild, FnrToggleItems } from "UI/FnrToggle2";
import { HoverSelectedBackground } from "UI/HoverChildren";
import { colors } from "UI/colors";

import TierPricingProvider, {
  useTierPricingContext,
} from "../TierPricingProvider";
import {
  useAddTier,
  useIsDisabled,
  useMaxCostHeaderText,
  useMaxTierCost,
} from "../tierPricingHooks";

import Tiers from "./Tiers/Tiers";

const TierPriceEditorContents = () => {
  const addTier = useAddTier();
  const addHeaderText = useMaxCostHeaderText();
  const [maxTierCost, setMaxTierCost] = useMaxTierCost();
  const {
    errorMessage,
    tierData,
    setTierData,
    entity,
    setErrorMessage,
    isFullWidth,
  } = useTierPricingContext();
  const [previousToggledTierData, setPreviousToggledTierData] = useState([]);
  const disabled = useIsDisabled();
  const removeAllTiers = () => {
    setErrorMessage("");
    setPreviousToggledTierData([...tierData]);
    const maxTier = tierData.find(({ numberOfSlots }) => !numberOfSlots);
    setTierData([maxTier]);
  };
  const toggleToTieredPricing = () => {
    setErrorMessage("");
    if (previousToggledTierData.length > 1)
      setTierData(previousToggledTierData);
    else addTier();
  };

  const [stagedMaxAmount, setStagedMaxAmount] = useState(maxTierCost);

  const flashPriceAnimation = useAnimation();

  const shouldFlashAmount =
    tierData &&
    tierData[tierData.length - 1] &&
    tierData[tierData.length - 1].shouldFlashAmount;

  useEffect(() => {
    if (shouldFlashAmount >= 1) {
      flashPriceAnimation.stop();
      flashPriceAnimation.start("show");
    }
  }, [flashPriceAnimation, shouldFlashAmount]);

  useEffect(() => {
    setTimeout(() => setStagedMaxAmount(maxTierCost));
  }, [maxTierCost, shouldFlashAmount]);

  const handleOnBlur = () => {
    setMaxTierCost(stagedMaxAmount);
  };

  const flashPriceAnimationComplete = (animation) => {
    if (animation === "show") flashPriceAnimation.start("hide");
  };

  return (
    <Wrapper>
      <ToggleWrapper>
        <FnrToggle
          style={{ border: "none", background: "none", padding: 0 }}
          hoverStyle={{ borderRadius: 8 }}
          CustomSelectedElement={ToggleCustomSelectedElement}
        >
          <FnrToggleItems
            style={{
              padding: 6,
              borderTopRightRadius: 15,
              borderTopLeftRadius: 15,
            }}
          >
            <FnrToggleChild selected={tierData.length <= 1}>
              <TieredPricingToggleButton
                onClick={removeAllTiers}
                disabled={tierData.length <= 1}
                type="button"
              >
                <FnrIcon fa-users fad /> One Price per Family
              </TieredPricingToggleButton>
            </FnrToggleChild>
            <FnrToggleChild selected={tierData.length > 1}>
              <TieredPricingToggleButton
                onClick={tierData.length <= 1 ? toggleToTieredPricing : null}
                disabled={tierData.length > 1}
                type="button"
              >
                <FnrIcon fa-layer-group fad /> Use Tiered Pricing
              </TieredPricingToggleButton>
            </FnrToggleChild>
          </FnrToggleItems>
        </FnrToggle>
      </ToggleWrapper>
      <Tiers />
      <TierAndMaxContainer $isFullWidth={isFullWidth}>
        {tierData.length <= 1 ? (
          <HouseholdLabelText>Price per Household</HouseholdLabelText>
        ) : (
          <MaxTierLabelText>
            <MaxTierNumber>
              {addHeaderText}
              <PlusSign />
            </MaxTierNumber>{" "}
            {entity}s
          </MaxTierLabelText>
        )}
        <PriceAround>
          <PriceContainer>
            <FlashPrice
              animate={flashPriceAnimation}
              initial={{ opacity: 0 }}
              variants={{
                show: { opacity: 1 },
                hide: { opacity: 0, transition: { duration: 2 } },
              }}
              onAnimationComplete={flashPriceAnimationComplete}
            />
            <FnrInput2
              type="currency"
              onChange={({ target: { value } }) => {
                setStagedMaxAmount(value);
              }}
              value={stagedMaxAmount}
              label={tierData.length > 1 ? "Total Price" : null}
              data-cy="TierPricing_MaxCostInput"
              onFocus={({ target }) => {
                target.select();
              }}
              onBlur={handleOnBlur}
              fullWidth
              disabled={disabled}
            />
          </PriceContainer>
          <PriceSpacer animate={{ width: tierData.length > 1 ? 28 : 0 }} />
        </PriceAround>
      </TierAndMaxContainer>
      <AnimatePresence>
        {errorMessage && (
          <ErrorTextWrapper
            initial={{ opacity: 0, height: 0, scale: 0.9, y: -12 }}
            animate={{ opacity: 1, height: "auto", scale: 1, y: 0 }}
            exit={{
              opacity: 0,
              height: 0,
              scale: 0.9,
              y: -12,
              transition: { type: "tween", duration: 0.2, ease: "easeOut" },
            }}
          >
            <ErrorTextPadding>
              <ErrorText>{errorMessage}</ErrorText>
            </ErrorTextPadding>
          </ErrorTextWrapper>
        )}
      </AnimatePresence>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  font-weight: 400;
`;

const TierAndMaxContainer = styled.div`
  display: flex;
  flex-direction: ${({ $isFullWidth }) => ($isFullWidth ? "row" : "column")};
  align-items: center;
  justify-content: ${({ $isFullWidth }) =>
    $isFullWidth ? "space-between" : "center"};
  overflow: hidden;
  padding: 16px;
`;
const TieredPricingToggleButton = styled.button`
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  border: none;
  width: 100%;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  cursor: pointer;
`;
const ToggleWrapper = styled.div`
  border-bottom: 1px solid ${colors.fnBorderGray};
`;
const ToggleCustomSelectedElement = styled(HoverSelectedBackground)`
  border-radius: 10px;
`;
const PriceAround = styled.div`
  display: flex;
`;
const PriceContainer = styled.div`
  max-width: 120px;
  position: relative;
`;
const PriceSpacer = styled(motion.div)`
  width: 0;
  flex-grow: 0;
  flex-shrink: 0;
`;
const MaxTierLabelText = styled.div`
  font-size: 20px;
  margin-left: 12px;
  display: flex;
  align-items: center;
`;
const MaxTierNumber = styled.div`
  font-size: 26px;
  width: 84px;
  text-align: center;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const PlusSign = styled.div`
  transform: translateY(-2px);
  margin-left: 2px;
  &::after {
    content: "+";
    font-size: 0.9em;
  }
`;
const HouseholdLabelText = styled.div`
  font-size: 18px;
`;

const FlashPrice = styled(motion.div)`
  position: absolute;
  background: rgba(252, 207, 107, 0.6);
  left: -12px;
  right: -12px;
  top: -12px;
  bottom: -12px;
  border-radius: 20px;
  opacity: 0;
`;
const ErrorTextWrapper = styled(motion.div)`
  overflow: hidden;
`;
const ErrorTextPadding = styled.div`
  padding: 6px;
`;
const ErrorText = styled.div`
  border-radius: 10px;
  padding: 8px 12px;
  color: #d5a223;
  border: 1px solid #d5a223;
  background: #f7f0b6;
  font-weight: 400;
`;

/**
 * @typedef {object} TierData
 * @property {number|string} amount
 * @property {number|string} id
 * @property {boolean} isNew
 * @property {number|string} numberOfSlots
 */

/**
 * @param {object} param0
 * @param {[TierData]} param0.tierData - the data the should currently be getting rendered
 * @param {function} param0.setTierData - called with the new TierData passed as the first arg when a change happens
 * @param {function} param0.setTierDataValid - called with a boolean value as the first arg decribing data validity when a change happens
 * @param {bool} param0.preventTierAnimation
 * @param {string} param0.entity
 */
const TierPriceEditor = ({
  tierData = [],
  setTierData,
  setTierDataValid,
  disabled,
  entity = "spot",
  preventTierAnimation,
}) => {
  return (
    <TierPricingProvider
      tierData={tierData}
      setTierData={setTierData}
      setTierDataValid={setTierDataValid}
      disabled={disabled}
      entity={entity}
      preventTierAnimation={preventTierAnimation}
    >
      <TierPriceEditorContents />
    </TierPricingProvider>
  );
};

TierPriceEditor.propTypes = {
  tierData: arrayOf(
    shape({
      numberOfSlots: oneOfType([number, string]),
      amount: oneOfType([number, string]),
      id: oneOfType([number, string]),
      ID: oneOfType([number, string]),
    })
  ),
  setTierData: func,
  setTierDataValid: func,
  disabled: bool,
};

export default TierPriceEditor;
